
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { loginUser } from './services/loginService';
import { saveAuthData } from './authUtils';
import { getDefaultNotificationPreferences } from '@/services/notificationService';
import { User } from './types';
import { useNavigate } from 'react-router-dom';
import { UserRoles } from '@/contexts/auth/enums';
import { isOrganizerProfileComplete } from '@/utils/profileUtils';

export const useLogin = (
  setUser: (user: User | null) => void,
  setToken: (token: string | null) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const login = async (email: string, password: string): Promise<boolean> => {
    // Ellenőrizzük, hogy már folyamatban van-e bejelentkezés
    if (isLoading) {
      console.log('Login already in progress, skipping new request');
      return false;
    }

    setIsLoading(true);

    try {
      const result = await loginUser(email, password);

      if (result.success && result.token && result.user) {
        // Ensure user has notification preferences
        if (!result.user.userProfile.notificationPreferences) {
          result.user.userProfile.notificationPreferences = getDefaultNotificationPreferences();
        }

        // Ensure the user has a valid profile
        if (!result.user.userProfile) {
          result.user.userProfile = {
            name: {
              firstName: '',
              lastName: ''
            }
          };
        }

        console.log('About to save auth data:', { token: result.token, user: result.user });
        saveAuthData(result.token, result.user);
        setToken(result.token);
        setUser(result.user);

        toast({
          title: "Sikeres bejelentkezés",
          description: `Üdvözöljük a MediHub rendszerében!`,
        });

        // Check if organizer with incomplete profile
        if (
          result.user.roles.includes(UserRoles.ORGANIZER) &&
          !isOrganizerProfileComplete(result.user)
        ) {
          // Store original intended destination to return after completing profile
          localStorage.setItem('redirect-after-profile', '/organizer/dashboard');

          // Redirect to profile page with a specific state parameter
          navigate('/organizer/profile', {
            state: {
              requireProfileCompletion: true,
              defaultTab: 'profile'
            }
          });

          // Show a notification about required profile completion
          toast({
            title: "Profil kitöltése szükséges",
            description: "Kérjük, töltse ki a profil adatait a folytatáshoz.",
          });
        } else {
          // Regular navigation based on user role
          if (result.user.roles.includes(UserRoles.ADMIN)) {
            navigate('/admin/dashboard');
          } else if (result.user.roles.includes(UserRoles.ORGANIZER)) {
            navigate('/organizer/dashboard');
          } else {
            // Regular user (participant)
            navigate('/');
          }
        }

        return true;
      } else {
        toast({
          title: "Bejelentkezési hiba",
          description: result.message || "Sikertelen bejelentkezés. Kérjük, ellenőrizze adatait.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Login processing error:', error);
      toast({
        title: "Bejelentkezési hiba",
        description: "Váratlan hiba történt. Kérjük, próbálja újra később.",
        variant: "destructive",
      });
      return false;
    } finally {
      // Késleltetett állapotváltás, hogy elkerüljük a gyors második kattintás problémáját
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  };

  return { login, isLoading };
};
